(() => {
  const chooser = document.getElementById('chooser');
  if (!chooser) return;

  const nativeFetch = window.fetch.bind(window);
  const worthResponseCache = new Map();
  const note = "I'd like to receive an email copy of my programme, along with a free maintenance calendar.";
  const supersededNotes = new Set([
    note,
    "I'd like to receive an email copy of my programme with a free calendar for maintenance."
  ]);
  let noteWriteInFlight = false;
  let discountApplyPromise = null;

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
    return String(input?.url || '');
  }

  function requestMethod(input, init) {
    if (init?.method) return String(init.method).toUpperCase();
    if (typeof Request !== 'undefined' && input instanceof Request) return String(input.method || 'GET').toUpperCase();
    return 'GET';
  }

  /* Repeated Worth including clicks used to consume repeated private-engine writes.
     Cache the two already-generated states in this tab. A fresh goals/areas/result request clears the cache. */
  window.fetch = async function ftaChooserPreviewFetch(input, init = {}) {
    const url = requestUrl(input);
    const method = requestMethod(input, init);
    const isChooserRequest = method === 'POST' && url.includes('/apps/fta-chooser/chooser/');

    if (isChooserRequest) {
      let parsedBody = null;
      if (typeof init?.body === 'string') {
        try { parsedBody = JSON.parse(init.body); } catch {}
      }

      const isWorthRequest = /\/chooser\/step(?:\?|$)/.test(url) && parsedBody?.action === 'worth' && typeof parsedBody.include === 'boolean';
      if (!isWorthRequest) worthResponseCache.clear();

      if (isWorthRequest && parsedBody.sessionId) {
        const key = `${parsedBody.sessionId}:${parsedBody.include}`;
        const cached = worthResponseCache.get(key);
        if (cached) {
          const replay = typeof structuredClone === 'function' ? structuredClone(cached) : JSON.parse(JSON.stringify(cached));
          if (Number.isInteger(parsedBody.version)) replay.version = parsedBody.version;
          return new Response(JSON.stringify(replay), {
            status: 200,
            headers: {'content-type': 'application/json', 'cache-control': 'no-store'}
          });
        }

        const response = await nativeFetch(input, init);
        if (response.ok) {
          try {
            const payload = await response.clone().json();
            if (payload?.programmeId && payload?.result) worthResponseCache.set(key, payload);
          } catch {}
        }
        return response;
      }
    }

    return nativeFetch(input, init);
  };

  function placeChooserInArticle() {
    const stage = chooser.closest('.fta-bioregulator-chooser-stage');
    if (!stage) return;
    const normalise = value => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const target = [...document.querySelectorAll('h3')].find(
      heading => !stage.contains(heading) && normalise(heading.textContent) === 'when a complete programme makes sense'
    );
    if (target?.parentNode) target.parentNode.insertBefore(stage, target);
  }

  function placeChooserOnProduct() {
    if (!/^\/products\/bioregulator-peptides-natures-marvels\/?$/.test(window.location.pathname)) return false;

    const stage = chooser.closest('.fta-bioregulator-chooser-stage');
    if (!stage) return false;

    const normalise = value => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const targetText = 'most people use several bioregulators simultaneously because they work together effectively.';

    const targets = [...document.querySelectorAll('p')].filter(
      paragraph => !stage.contains(paragraph) && normalise(paragraph.textContent) === targetText
    );

    const isVisible = element => {
      if (!element.isConnected) return false;
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    };

    const target = targets.find(paragraph => !paragraph.closest('.responsive-tabs') && isVisible(paragraph))
      || targets.find(paragraph => !paragraph.closest('.responsive-tabs'))
      || targets.find(isVisible);
    if (!target?.parentNode) return false;

    if (target.nextElementSibling !== stage) target.after(stage);
    return true;
  }

  function scheduleProductPlacement() {
    if (!/^\/products\/bioregulator-peptides-natures-marvels\/?$/.test(window.location.pathname)) return;
    [0, 50, 150, 300, 700, 1500].forEach(delay => {
      window.setTimeout(() => { placeChooserOnProduct(); }, delay);
    });
  }

  function cartRoot() {
    const root = window.Shopify?.routes?.root;
    return typeof root === 'string' && root.startsWith('/') && root.endsWith('/') ? root : '/';
  }

  function withoutRequestedNote(value) {
    const kept = String(value || '').split(/\r?\n/).filter(line => !supersededNotes.has(line.trim()));
    while (kept.length && !kept[0].trim()) kept.shift();
    while (kept.length && !kept[kept.length - 1].trim()) kept.pop();
    return kept.join('\n');
  }

  async function cartRequest(path, options = {}) {
    const response = await fetch(`${cartRoot()}${path}`, {
      method: options.method || 'GET',
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(options.body ? {'content-type': 'application/json'} : {})
      },
      body: options.body ? JSON.stringify(options.body) : undefined
    });
    if (!response.ok) throw new Error('Cart update failed');
    return response.json();
  }

  async function syncCartNote(input) {
    if (noteWriteInFlight) return;
    noteWriteInFlight = true;
    const wanted = input.checked;
    const status = input.closest('.shop-panel')?.querySelector('.cart-status');
    input.disabled = true;
    if (status) status.textContent = wanted ? 'Adding order note…' : 'Removing order note…';

    try {
      const cart = await cartRequest('cart.js');
      const base = withoutRequestedNote(cart.note);
      const nextNote = wanted ? [base, note].filter(Boolean).join('\n') : base;
      await cartRequest('cart/update.js', {method: 'POST', body: {note: nextNote}});
      if (status) status.textContent = wanted ? 'Order note added.' : 'Order note removed.';
    } catch {
      input.checked = !wanted;
      if (status) status.textContent = 'We couldn’t update the order note. Please try again.';
    } finally {
      input.disabled = false;
      noteWriteInFlight = false;
    }
  }

  function applyCartNotePrompt() {
    const input = chooser.querySelector('[data-planner-request]');
    const label = input?.closest('label.planner-request');
    const span = label?.querySelector('span');
    if (!input || !span) return;

    if (span.dataset.ftaCartNotePrompt !== 'v2') {
      const strong = document.createElement('strong');
      strong.className = 'fta-order-note-prefix';
      strong.textContent = 'Add this note to your order:';
      const em = document.createElement('em');
      em.textContent = note;
      span.replaceChildren(strong, document.createTextNode(' '), em);
      span.dataset.ftaCartNotePrompt = 'v2';
    }

    if (input.dataset.ftaCartNoteWired === 'true') return;
    input.dataset.ftaCartNoteWired = 'true';
    input.addEventListener('change', () => { void syncCartNote(input); });
  }

  async function applyBioregOffer() {
    if (discountApplyPromise) return discountApplyPromise;
    discountApplyPromise = cartRequest('cart/update.js', {
      method: 'POST',
      body: {discount: 'BIOREG-OFFER'}
    }).then(() => true).catch(() => {
      discountApplyPromise = null;
      return false;
    });
    return discountApplyPromise;
  }

  function finishCartStatus(status, message) {
    if (status.dataset.ftaBasketLink === message) return;
    status.dataset.ftaBasketLink = message;
    const link = document.createElement('a');
    link.href = '/discount/BIOREG-OFFER?redirect=%2Fcart';
    link.textContent = 'basket';
    status.replaceChildren(document.createTextNode(`${message}. Go to `), link);
  }

  function enhanceCartStatuses() {
    chooser.querySelectorAll('.cart-status').forEach(status => {
      if (status.dataset.ftaBasketObserver === 'true') return;
      status.dataset.ftaBasketObserver = 'true';
      const observer = new MutationObserver(() => {
        const text = status.textContent.trim();
        if (text !== 'Added to basket' && text !== 'Already in basket') return;
        const message = text;
        void applyBioregOffer().finally(() => finishCartStatus(status, message));
      });
      observer.observe(status, {childList: true, subtree: true, characterData: true});
    });
  }

  function applyBrandRefinement() {
    const mark = chooser.querySelector('.chooser-mark');
    const title = chooser.querySelector('.chooser-brand strong');
    if (mark) {
      mark.style.width = '28px';
      mark.style.height = '30px';
      mark.style.flexBasis = '28px';
    }
    const chooserTitle = 'Nature’s Marvels bioregulator chooser';
    if (title && title.textContent !== chooserTitle) title.textContent = chooserTitle;
  }

  function addFinalPreviewStyles() {
    if (document.getElementById('ftaChooserFinalPreviewStyles')) return;
    const style = document.createElement('style');
    style.id = 'ftaChooserFinalPreviewStyles';
    style.textContent = `
      .chooser{--interaction:var(--cta)!important;--interaction-hover:var(--amaranth)!important;--selected-green:var(--cta)!important;}

      #chooser .chooser-brand strong{font-weight:600!important;}
      #chooser .goal-grid .choice{cursor:pointer!important;}
      #chooser .goal-grid .choice > *{pointer-events:none!important;}

      .chooser #step2 .area-row{padding-top:24px!important;padding-bottom:24px!important;}
      .chooser #step2 .unsure-line{padding-top:24px!important;}

      .chooser .planner-request input[type="checkbox"]{
        width:16px!important;height:16px!important;min-width:16px!important;min-height:16px!important;
        margin:2px 0 0!important;transform:none!important;box-shadow:none!important;
        appearance:auto!important;-webkit-appearance:checkbox!important;accent-color:var(--cta)!important;
      }

      /* One interaction palette throughout: light amaranth selected, full amaranth on hover. */
      .chooser .relation{color:var(--ink)!important;font-size:.75rem!important;line-height:1.2!important;gap:5px!important;}
      .chooser .relation::before{color:var(--cta)!important;font-size:1em!important;line-height:1!important;}
      .chooser .choice[aria-pressed="true"],
      .chooser .chip[aria-pressed="true"],
      .chooser .shop-options .order-action[aria-pressed="true"]{
        border-color:var(--cta)!important;color:var(--cta)!important;
      }
      .chooser .choice[aria-pressed="true"] .choice-dot:after{background:var(--cta)!important;}
      .chooser .group-choice[aria-pressed="true"] .group-action,
      .chooser .group-choice[aria-pressed="mixed"] .group-action{color:var(--cta)!important;}
      .chooser .choice:hover,
      .chooser .chip:hover,
      .chooser .shop-options .order-action:hover,
      .chooser .route-choice:hover{border-color:var(--amaranth)!important;color:var(--amaranth)!important;}
      .chooser .group-choice:hover .group-action{color:var(--amaranth)!important;}
      .chooser .support-toggle[aria-checked="true"] .support-toggle-track{background:var(--cta)!important;border-color:var(--cta)!important;}
      .chooser .support-toggle[aria-checked="true"]:hover .support-toggle-track{background:var(--amaranth)!important;border-color:var(--amaranth)!important;}
      .chooser .support-toggle[aria-checked="false"]:hover .support-toggle-track{border-color:var(--amaranth)!important;}
      .chooser .progress-item.current{color:var(--cta)!important;}
      .chooser .progress-item.current:after,.chooser .progress-item.done:after{background:var(--cta)!important;}

      .chooser .email-preview-details,
      .chooser .email-preview-details p,
      .chooser .email-preview-details span,
      .chooser .email-preview-details li,
      .chooser .email-preview-details td,
      .chooser .email-preview-details h4,
      .chooser .email-preview-details h5,
      .chooser .email-takeaway-kicker,
      .chooser .email-meta,
      .chooser .email-cycle,
      .chooser .email-programme-group>span,
      .chooser .programme-group-title,
      .chooser .email-broad-note,
      .chooser .email-footer{font-size:.875rem!important;}
      .chooser .email-preview-details h4,
      .chooser .email-preview-details h5,
      .chooser .programme-group-title{font-weight:600!important;}

      /* More editorial result cards: a little more air above the image, much less below it. */
      .chooser .result-visual{padding:26px 18px 0!important;min-height:0!important;}
      .chooser .product-image{margin:4px 0 -10px!important;}
      .chooser .result-body{padding-top:0!important;}
      .chooser .result-body h4{margin:0 0 1px!important;line-height:1.25!important;}
      .chooser .result-reason{margin:0 0 8px!important;}

      .chooser .fta-inline-programme-status{
        display:block!important;margin:22px 0 26px!important;padding:10px 12px!important;
        border-left:2px solid var(--cta);background:#fff7f8;color:var(--ink)!important;
        font-size:.875rem!important;line-height:1.45!important;
      }

      /* Give the product title clear breathing room below the image. */
      #chooser .result-visual{padding:20px 18px 10px!important;min-height:0!important;}
      #chooser .product-image{margin:0!important;}
      #chooser .result-body{padding-top:60px!important;}
    `;
    document.head.appendChild(style);
  }

  const serviceStatus = chooser.querySelector('#chooserServiceStatus');
  const serviceStatusHome = serviceStatus?.parentNode || null;
  const serviceStatusAnchor = serviceStatus?.previousElementSibling || null;

  function isProgrammeUpdateMessage(text) {
    return /couldn.?t update your programme|please wait a moment/i.test(String(text || ''));
  }

  function placeServiceStatus() {
    if (!serviceStatus || !serviceStatusHome) return;
    const text = serviceStatus.textContent?.trim() || '';
    const results = chooser.querySelector('#results');
    const worthSection = chooser.querySelector('#worthIncludingTitle')?.closest('.also');
    const shouldInline = Boolean(text && isProgrammeUpdateMessage(text) && results?.classList.contains('active') && worthSection?.parentNode);

    if (shouldInline) {
      if (serviceStatus.parentNode !== worthSection.parentNode || serviceStatus.previousElementSibling !== worthSection) {
        worthSection.after(serviceStatus);
      }
      if (!serviceStatus.classList.contains('fta-inline-programme-status')) {
        serviceStatus.classList.add('fta-inline-programme-status');
      }
      return;
    }

    if (serviceStatus.classList.contains('fta-inline-programme-status')) {
      serviceStatus.classList.remove('fta-inline-programme-status');
    }
    if (serviceStatus.parentNode !== serviceStatusHome) {
      if (serviceStatusAnchor?.parentNode === serviceStatusHome) serviceStatusAnchor.after(serviceStatus);
      else serviceStatusHome.prepend(serviceStatus);
    }
  }

  function restoreSupportToggles() {
    if (chooser.getAttribute('aria-busy') === 'true') return;
    chooser.querySelectorAll('.support-toggle[disabled]').forEach(toggle => { toggle.disabled = false; });
  }

  function applyOneMonthDefault() {
    const monthCards = chooser.querySelectorAll('#programmeContent .programme-card');
    if (monthCards.length !== 1) return;
    const monthOne = chooser.querySelector('[data-order-option="month1"]');
    if (!monthOne || monthOne.getAttribute('aria-pressed') === 'true' || chooser.getAttribute('aria-busy') === 'true') return;
    monthOne.click();
  }

  function applyPreviewFixes() {
    applyBrandRefinement();
    applyCartNotePrompt();
    enhanceCartStatuses();
    restoreSupportToggles();
    applyOneMonthDefault();
    placeServiceStatus();
  }

  chooser.addEventListener('click', event => {
    const toggle = event.target.closest('.support-toggle');
    if (!toggle || !chooser.contains(toggle)) return;
    if (serviceStatus && isProgrammeUpdateMessage(serviceStatus.textContent)) {
      serviceStatus.textContent = '';
      serviceStatus.hidden = true;
      placeServiceStatus();
    }
  }, true);

  placeChooserInArticle();
  scheduleProductPlacement();
  addFinalPreviewStyles();
  applyPreviewFixes();
  new MutationObserver(applyPreviewFixes).observe(chooser, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-busy', 'class', 'disabled', 'hidden']
  });
})();
